import * as React from 'react';
import bind from 'bind-decorator';
import { toast } from 'react-toastify';
import { Alert, Dropdown, Form, Tab, Tabs } from 'react-bootstrap';
import Select from 'react-dropdown-select';
import { ExcelData, LeadImportType } from 'Interfaces/ImportDataInterfaces';
import ReactGA from 'react-ga';
import { questionTypes } from 'Interfaces/Forms/QuestionInterface';
import { FormInterface } from '../../../Interfaces/Forms/FormsInterface';
import { LooseObject } from '../../../Interfaces/LooseObject';
import { ClientPersistInterface } from '../../../Interfaces/ClientPersistInterface';
import { getLocalization } from '../../../global/global';
import GenericModal from '../../Modals/GenericModal';
import ImportPreview from './ImportPreview';
import ImportFileSelector from './ImportFileSelector';

interface Props {
  forms: FormInterface[];
  clientPersist: ClientPersistInterface;
  loadImportMapping: (formIds: string[]) => void;
  importMappings: LooseObject | null;
  uploadDataImport: (
    file: File, mappings: LooseObject, formId: string, geocode: boolean, newUpdate: LeadImportType
  ) => void;
  uploadImportExcelFile: (file: File, signal: AbortSignal) => Promise<Response>;
  importData: (excelData: ExcelData) => void;
}

interface State {
  showModal: false;
  name: string;
  file: File | null;
  form: string;
  geocode: boolean;
  showGeocode: boolean;
  importMappings: LooseObject | null;
  selections: string[];
  fileTypeError: boolean;
  selectedTab: 'csv' | 'excel';
  excelData: ExcelData | null;
  preview: boolean;
  noName: boolean;
  crmFormType: string;
  fileData: string;
  importNewUpdate: LeadImportType;
  delimeter: string;
}

export default class ImportDataComponent extends React.Component<Props, State> {
  private fileInput = React.createRef<HTMLInputElement>();
  private questions: LooseObject = [];
  private fileRow: string[] = [];
  private tempData: string[][] = [];
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      name: '',
      file: null,
      geocode: false,
      showGeocode: false,
      form: '',
      importMappings: props.importMappings,
      selections: [],
      fileTypeError: false,
      selectedTab: 'csv',
      excelData: null,
      preview: false,
      noName: false,
      crmFormType: '',
      fileData: '',
      importNewUpdate: '',
      delimeter: ''
    };
    this.fileInput = React.createRef();
  }

  @bind
  private showImportModal(visible) {
    this.fileRow = [];
    this.tempData = [];
    this.setState({ showModal: visible, preview: false, file: null });
  }

  @bind
  private handleChange(e) {
    const state: Partial<State> = {};
    state[e.target.name] = e.target.value;
    if (e.target.name === 'form' && e.target.value !== '') {
      const form = this.props.forms.find(f => f.ref === e.target.value);
      state.crmFormType = form?.crmFormType || '';
      if (this.state.file) {
        this.setState({...this.state, ...state}, this.loadQuestions);
      } else {
        this.setState({...this.state, ...state});
      }
    } else {
      if (e.target.name === 'form') {
        state.crmFormType = '';
        state.importNewUpdate = '';
      }
      this.setState({...this.state, ...state});
    }
  }

  @bind
  private handleCheckChange(e) {
    const state = {};
    state[e.target.name] = e.target.checked;
    this.setState(state);
  }

  @bind
  private setImportParams(fileRow: string[], tempData: string[][], file: File, delimeter: string) {
    this.fileRow = fileRow;
    this.tempData = tempData;
    this.setState({ file, delimeter });
    if (this.state.form !== '') {
      this.loadQuestions();
    }
  }

  @bind
  private loadQuestions() {
    const { form, importMappings } = this.state;
    const defaultSelections: string[] = [];
    const mappings = importMappings && importMappings[form] ? JSON.parse(importMappings[form]) : [];
    const formJSON = this.props.forms.find(f => f.ref === form);
    if (formJSON) {
      const qns: LooseObject[] = [];
      const allowedQuestions = [
        'IntQuestion', 'StringQuestion', 'FloatQuestion', 'NameQuestion', 'SelectMultipleQuestion', 'UrlQuestion',
        'SelectOneQuestion', 'BooleanQuestion', 'DateQuestion', 'StatusQuestion', 'LikertScaleQuestion',
        'EmailQuestion', 'PhoneNumberQuestion',
        questionTypes.CALCULATED_VALUE_QUESTION, questionTypes.LOOKUP_VALUES_QUESTION
      ];
      const getQuestion = (questions: LooseObject[]): LooseObject | undefined => {
        for (const question of questions) {
          if (!question.inVisible && !question.deleted) {
            if (allowedQuestions.indexOf(question.type) !== -1) {
              qns.push({ id: question.id, type: question.type, text: question.text });
              const index = this.fileRow.findIndex(
                (t) => t.trim().toLowerCase() === `${question.text}`.trim().toLowerCase()
              );
              if (index > -1) {
                defaultSelections[index] = question.id;
              } else if (mappings.length) {
                const mapped = mappings.find(m => m.questionId === question.id);
                if (mapped) {
                  const ind = this.fileRow.findIndex(f => f.trim().toLowerCase() === mapped.columnName);
                  if (ind > -1) {
                    defaultSelections[ind] = question.id;
                  }
                }
              }
              if (
                question.triggerValues &&
                question.triggerValues.triggerValue
              ) {
                for (const triggerValue of question.triggerValues.triggerValue) {
                  if (triggerValue.action.subQuestions) {
                    getQuestion(triggerValue.action.subQuestions.question);
                  }
                }
              }
            }
            if (question.type === 'GPSQuestion' && formJSON.hasCoordinates) {
              qns.push({ id: 'Lat', type: 'GPSQuestion', text: 'Latitude' });
              qns.push({ id: 'Lon', type: 'GPSQuestion', text: 'Longitude' });
            }
          }
        }
        return undefined;
      };

      qns.push({ id: 'scheduledate', type: '', text: getLocalization('scheduleDate') });
      qns.push({ id: 'modified', type: '', text: getLocalization('modifiedOn') });
      if (formJSON.hasLocationHierarchy === true) {
        const { clientPersist } = this.props;
        const labels = clientPersist.locationLabels.length > 0 ?
          clientPersist.locationLabels : ['Location 1', 'Location 2', 'Location 3', 'Location 4'];
        labels.forEach((label, index) => {
          const ind = this.fileRow.indexOf(label);
          if (ind > -1) {
            defaultSelections[ind] = `location${index + 1}`;
          }
          qns.push({ id: `location${index + 1}`, type: '', text: label });
        });
      }
      if (formJSON.questionPage) {
        for (const page of formJSON.questionPage) {
          getQuestion(page.question || []);
        }
      }
      this.questions = qns;
      this.setState({ selections: defaultSelections, crmFormType: formJSON.crmFormType || '' });
    }
  }

  @bind
  private importData() {
    const { selections, form, file, geocode, selectedTab, excelData, importNewUpdate, delimeter } = this.state;
    if (selectedTab === 'csv') {
      const mappings: LooseObject[] = [];
      let i = 0;
      if (file && form !== '') {
        const coord = {};
        while (i < selections.length) {
          if (selections[i]) {
            const map = {};
            map['index'] = i;
            map['questionId'] = selections[i];
            map['columnName'] = this.fileRow[i];
            const question = this.questions.find(q => q.id === selections[i]);
            if (question) {
              map['questionType'] = question.type;
            }
            if (map['questionType'] === 'GPSQuestion') {
              coord[selections[i]] = map;
            } else {
              mappings.push(map);
            }
          }
          i++;
        }
        if (coord['Lat'] && coord['Lon']) {
          mappings.push(coord);
        }
        this.props.uploadDataImport(file, mappings, form, geocode, importNewUpdate, delimeter);
        this.setState({ showModal: false });
      }
      console.log('import data');
    } else {
      if (excelData) {
        this.props.importData(excelData);
        this.setState({ showModal: false });
      }
    }
  }

  @bind
  private onFileSelectClick() {
    if (this.fileInput.current) {
      this.fileInput.current.click();
    }
  }

  @bind
  private loadImportMapping() {
    if (!this.state.importMappings) {
      const formIds = this.props.forms.map((f) => {
        if (!f.isChild && f.type === 'POI') {
          return f.ref;
        }
        return '';
      }).filter( id => id !== '');
      this.props.loadImportMapping(formIds);
    } else {
      this.showImportModal(true);
    }
    const { clientPersist } = this.props;
    if (clientPersist.payingCustomer === 'yes') {
      ReactGA.event({
        category: 'module-selection',
        action: `import-data`,
        label: `${clientPersist.instance}`
      });
    }
  }

  @bind
  private getModalContent() {
    const { forms } = this.props;
    const { preview, selectedTab, form, selections, crmFormType, importNewUpdate } = this.state;
    if (preview && selectedTab === 'csv') {
      return (
        <ImportPreview
          formId={form}
          columns={this.fileRow}
          values={this.tempData}
          questionIds={selections}
        />
      );
    }
    return (
      <Tabs
        defaultActiveKey="csv"
        id="uncontrolled-tab-example"
        onSelect={(key) => { if (key === 'csv' || key === 'excel') { this.setState({ selectedTab: key }); }}}
      >
        <Tab eventKey="csv" title={getLocalization('csvFile')}>
          <div>
            <Alert variant={'info'} className="mt-3">
              <p className="text-danger mb-0">{getLocalization('importQuestionType')}</p>
              <p className="text-info mb-0">{getLocalization('importFileInfo')}</p>
              <p className="text-info mb-0">{getLocalization('scheduleDateInfo')}</p>
              <p className="text-info mb-0">{getLocalization('importStatusValues')}</p>
            </Alert>
            <div className="form-group mb-3">
              <label className="form-label">{getLocalization('selectform')}</label>
              <select
                className="form-control input-sm"
                onChange={this.handleChange}
                value={form}
                name="form"
              >
                <option value="">{getLocalization('selectform')}</option>
                {forms.map(f => {
                  return f.type === 'POI' && !f.isChild ? (<option value={f.ref}>{f.name}</option>) : undefined;
                }).filter( f => f !== undefined)}
              </select>
            </div>
            <ImportFileSelector
              setImportParams={this.setImportParams}
            />
            {crmFormType === 'person' && (
              <>
                <Form.Check
                  id={'import-new-dp'}
                  label={getLocalization('importNew')}
                  checked={importNewUpdate === 'new'}
                  name={'importNewUpdate'}
                  value={'new'}
                  onChange={(e) => {
                    this.setState({ importNewUpdate: e.target.checked ? 'new' : ''});
                  }}
                />
                <Form.Check
                  id={'import-update-dp'}
                  label={getLocalization('updateExistingData')}
                  checked={importNewUpdate === 'update'}
                  name={'importNewUpdate'}
                  value={'update'}
                  onChange={(e) => {
                    this.setState({ importNewUpdate: e.target.checked ? 'update' : ''});
                  }}
                />
              </>
            )}
            <div className="container-fluid import-questions">
              {this.getImportRows()}
            </div>
            {this.state.showGeocode && (
              <div className="checkbox">
                <label>
                  <input
                    onChange={this.handleCheckChange}
                    name="geocode"
                    type="checkbox"
                    checked={this.state.geocode}
                  />{getLocalization('geocode')}
                </label>
              </div>
            )}
          </div>
        </Tab>
        {/*
          Removed: no longer used as was needed by MCI Ghana only
        <Tab eventKey="excel" title="Excel file">
          <ImportDataExcelComponent
            forms={forms}
            uploadImportExcelFile={this.props.uploadImportExcelFile}
            setExcelData={this.setExcelData}
            clientPersist={this.props.clientPersist}
          />
        </Tab>
        */}
      </Tabs>
    );
  }

  @bind
  private onQuestionSelected(id, index) {
    const addressFields = ['address', 'street', 'city', 'zipcode', 'state'];
    const selections = [...this.state.selections];
    selections[index] = id;
    const state = {};
    state['selections'] = selections;
    if (addressFields.indexOf(id) !== -1) {
      const { form } = this.state;
      const { clientPersist } = this.props;
      const formModel = this.props.forms.find(f => f.ref === form);
      if (formModel && formModel.includeAddress &&
        (clientPersist.package !== 'Light' || clientPersist.instance === 'airliquide')) {
        state['showGeocode'] = true;
      }
    }
    this.setState(state);
  }

  private getImportRows() {
    const { selections } = this.state;
    const rows = this.fileRow.map((row, index) => {
      const selected = selections[index] ? this.questions.filter(q => q.id === selections[index]) : [];
      return (
        <div className="row pt-2 border-top" key={`${index}`}>
          <div className="col-md-1">{(index + 1)}</div>
          <div className="col-md-5">
            {row}
          </div>
          <div className="col-md-6 form-group">
            <Select
              key={`impoprt-question-dropdown-Select-${index}`}
              labelField={'text'}
              valueField={'id'}
              values={selected}
              multi={false} // { title: 'Select one', key: 'NULL' }
              options={
                this.questions.map((q) => {
                  return selections.indexOf(q.id) === -1 || selections[index] === q.id ? ({...q}) : null;
                }).filter(q => q)}
              onChange={(v) => v.length > 0 ?
                this.onQuestionSelected(v[0].id, index) : this.onQuestionSelected('', index)
              }
              searchable={true}
              searchBy={'text'}
            />
          </div>
        </div>
      );
    });
    return rows;
  }

  @bind
  private setExcelData(excelData: ExcelData | null) {
    this.setState({ excelData });
  }

  @bind
  private validateName() {
    const hasName = this.state.selections.find(s => 'name' === s?.toLowerCase());
    this.setState({ noName: !hasName });
    if (!hasName) {
      toast(getLocalization('nameImport'), {
        type: toast.TYPE.ERROR,
        autoClose: false,
      });
      return false;
    }
    if (this.state.crmFormType === 'person') {
      const hasCompany = this.state.selections.find(s => 'location1' === s?.toLowerCase());
      if (!hasCompany) {
        toast(getLocalization('companyRequired'), {
          type: toast.TYPE.ERROR,
          autoClose: false,
        });
        return false;
      }
    }
    return true;
  }

  public static getDerivedStateFromProps(props: Props, state: State) {
    if (props.importMappings && !state.importMappings) {
      return { importMappings: props.importMappings, showModal: true };
    }
    return null;
  }

  public render(): JSX.Element {
    const { preview } = this.state;
    const modal = this.state.showModal ? (
      <GenericModal
        visible={this.state.showModal}
        body={this.getModalContent()}
        onConfirm={() => {
          this.setState({ preview: !preview }, () => {
            if (this.state.preview && this.state.crmFormType) {
              this.validateName();
            }
            if (!this.state.preview) {
              const valid = this.state.crmFormType ? this.validateName() : true;
              if (valid) {
                this.importData();
              }
            }
          });
        }}
        confirmText={getLocalization('importTitle')}
        title={getLocalization('importTitle')}
        cancel={() => {
          if (!this.state.preview) {
            this.showImportModal(false);
          } else {
            this.setState({ preview: false });
          }
        }}
        cancelText={getLocalization('cancel')}
        dialogClassName={'large-modal'}
      />
    ) : null;

    return (
      <React.Fragment>
        {modal}
        <Dropdown.Item
          eventKey="2"
          onClick={() => this.loadImportMapping()}
          id={'import-data-btn'}
        >
          {getLocalization('importData')}
        </Dropdown.Item>
      </React.Fragment>
    );
  }
}
